import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useServerTable } from '@/components/data-grid';
import { customInstance, http } from '@/api/http';
import { selectData, type PageResponse, type SuccessResponseDTO } from '@/api/envelope';
import { searchModerationItems, useGetReportTypes } from '@/api/generated/moderation/moderation';
import type {
  ItemResponseDTO,
  ModerationMetricsResponseDTO,
  SearchModerationItemsParams,
  SearchRequestDTO,
  SearchRequestDTOContentType,
  SearchRequestDTOReportType,
  SearchRequestDTOReportStatus,
  SuccessResponseDTOListMapStringStringDataItem,
} from '@/api/generated/model';
import type { TimeRangeBounds } from '@/lib/timeRangeOptions';
import { apiCountryCode, geoRegionCode } from '@/lib/geoSelection';
import type { ModerationDashboardResponseDTO } from './dashboardTypes';
import type { ModerationIntelligence } from './types';

/**
 * Thin data-layer wrappers over the generated moderation orval fetchers/hooks (doc 09 §4.1/§4.3).
 * Row-mapping is NOT done here — the DataGrid columns read raw ItemResponseDTO rows (columns.tsx).
 */

/** Stable base key for the report queue — used for invalidation after mutations (doc 09 §4.3). */
export const MODERATION_LIST_KEY = ['moderation', 'items'] as const;

/**
 * List: server-paginated report queue wired into the shared useServerTable (doc 05). Column filters
 * are pushed to the server as contentType / reportType / reportStatus query params — this fixes the
 * CMP client-only filter bug (doc 09 §4.1 / §8 risk #1). Each filter is single-value; the first
 * selected option wins.
 */
/**
 * User-report queue backed by the admin moderation tables (`GET /admin/api/moderation/items`).
 * Used by ReportedPostsPage. The Coop pipeline dashboard must use `useCoopModerationCases` instead.
 */
export function useModerationList() {
  return useServerTable<ItemResponseDTO>({
    queryKey: MODERATION_LIST_KEY,
    fetchPage: async ({ pageParam, pageSize, search, filters }) => {
      // Real spec nests all list criteria under a single `searchRequestDTO` query object.
      const searchRequestDTO: SearchRequestDTO = { pageNumber: pageParam, pageSize };
      const keyword = search.trim();
      if (keyword) searchRequestDTO.keyword = keyword;
      // Filter columnKeys carry the real enum codes (see ModerationListPage options), so narrow to them.
      const contentType = filters.type?.[0];
      if (contentType) searchRequestDTO.contentType = contentType as SearchRequestDTOContentType;
      const reportType = filters.reportType?.[0];
      if (reportType) searchRequestDTO.reportType = reportType as SearchRequestDTOReportType;
      const reportStatus = filters.status?.[0];
      if (reportStatus)
        searchRequestDTO.reportStatus = reportStatus as SearchRequestDTOReportStatus;

      const params: SearchModerationItemsParams = { searchRequestDTO };
      const res = await searchModerationItems(params);
      const page = selectData(res);
      // Envelope `data` may be absent -> return an empty terminal page (shared contract).
      return page ?? { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
    },
    pageSize: 10, // parity with CMP (ModerationViewModel.kt:106)
    mode: 'paged',
    urlKey: 'reports',
  });
}

/**
 * Report types for the Violation filter (op getReportTypes). Real spec envelopes a
 * List<Map<String,String>> (each map exposes the code/label pair) -> unwrap with selectData.
 */
/**
 * Report types for the User Reports Violation filter (`getReportTypes`).
 * Coop policy data is exposed separately by `useCoopModerationRules`.
 */
export function useReportTypes() {
  return useGetReportTypes<SuccessResponseDTOListMapStringStringDataItem[]>({
    query: { select: selectData },
  });
}

/**
 * New endpoints (doc: Moderation detail actions + list metrics) are NOT in the orval-generated client
 * yet — call them via the shared `http` axios instance + `selectData`, wrapped in react-query like the
 * generated hooks above (CONTRACTS.md §2/§3). Do NOT run orval/codegen for these.
 */

export interface SendModerationNotificationInput {
  userId: number;
  title: string;
  message: string;
}

/** POST /admin/api/moderation/send-notification — real in-app notification (Task B). */
export function useSendModerationNotification() {
  return useMutation({
    mutationFn: async (data: SendModerationNotificationInput) => {
      const res = await http.post<SuccessResponseDTO<void>>(
        '/admin/api/moderation/send-notification',
        data,
      );
      return selectData<void>(res.data);
    },
  });
}

/** DELETE /admin/api/moderation/report/{id}/message — hard-deletes the reported message (Task C). */
export function useDeleteReportedMessage() {
  return useMutation({
    mutationFn: async (reportId: number) => {
      const res = await http.delete<SuccessResponseDTO<void>>(
        `/admin/api/moderation/report/${reportId}/message`,
      );
      return selectData<void>(res.data);
    },
  });
}

/**
 * Extended metrics type — the base DTO comes from the generated orval model; Coop automation
 * metrics are appended by the backend once the pipeline integration is live. Fields are optional
 * so the UI degrades gracefully against an older backend that hasn't shipped them yet.
 */
export type ModerationMetrics = ModerationMetricsResponseDTO & {
  /** Reports auto-approved by Coop today. */
  autoApprovedToday?: number;
  /** Reports auto-rejected by Coop today. */
  autoRejectedToday?: number;
  /** Cases routed to risk-tiered analysis today (PENDING records with a non-null risk tier). */
  gpuEscalationsToday?: number;
  /** Cases currently waiting in the human/specialist review queue. */
  inHumanQueue?: number;
};

export interface CoopModerationMetrics {
  autoApprovedToday?: number;
  autoRejectedToday?: number;
  /** @deprecated Alias of frontierQueue — live PENDING+risk_tier depth, not same-day events. */
  gpuEscalationsToday?: number;
  /** Live PENDING rows with a risk tier (frontier / GPU path). */
  frontierQueue?: number;
  /** Live PENDING rows without a risk tier (automated processing). */
  processingQueue?: number;
  inHumanQueue?: number;
  /** Live ERROR rows still open. */
  errorQueue?: number;
  /** Live undecided cases (excludes terminated posts). */
  activeCases?: number;
}

/**
 * Primary Coop moderation metrics. The admin API route is an authenticated proxy; the values are
 * sourced from the Coop moderation publication mirror, not the legacy report tables.
 */
export function useCoopModerationMetrics(options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: ['moderation', 'coop', 'metrics'],
    queryFn: async (): Promise<CoopModerationMetrics> => {
      const res = await http.get<SuccessResponseDTO<CoopModerationMetrics>>(
        '/admin/api/moderation/coop/metrics',
      );
      return selectData<CoopModerationMetrics>(res.data) ?? {};
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * @deprecated Mixed legacy report metrics. Use `useCoopModerationMetrics` for the moderation
 * dashboard; this hook remains only for legacy report-queue consumers.
 */
export function useModerationMetrics() {
  return useQuery({
    queryKey: ['moderation', 'metrics'],
    queryFn: async (): Promise<ModerationMetrics> => {
      const res = await http.get<SuccessResponseDTO<ModerationMetrics>>(
        '/admin/api/moderation/metrics',
      );
      return selectData<ModerationMetrics>(res.data);
    },
    staleTime: 30_000,
  });
}

// --- Coop Intelligence endpoints (Phase 1) ---

/** Query key for the intelligence endpoint — used for invalidation and refetch control. */
export const MODERATION_INTELLIGENCE_KEY = (id: number) =>
  ['moderation', 'intelligence', id] as const;

/** GET /admin/api/moderation/report/{id}/intelligence — fetch Coop intelligence data. */
export async function fetchModerationIntelligence(
  reportId: number,
): Promise<ModerationIntelligence> {
  const res = await http.get<SuccessResponseDTO<ModerationIntelligence>>(
    `/admin/api/moderation/report/${reportId}/intelligence`,
  );
  return selectData<ModerationIntelligence>(res.data);
}

export interface ModerationOverrideInput {
  action: string;
  reason?: string;
  /** Hard-gate Policies selected on Human Review Reject (e.g. Child Safety). */
  violatedPolicies?: string[];
}

/** POST /admin/api/moderation/report/{id}/override — override Coop verdict. */
export function useModerationOverride() {
  return useMutation({
    mutationFn: async ({ reportId, data }: { reportId: number; data: ModerationOverrideInput }) => {
      const res = await http.post<SuccessResponseDTO<void>>(
        `/admin/api/moderation/report/${reportId}/override`,
        data,
      );
      return selectData<void>(res.data);
    },
  });
}

// --- Proactive Moderation endpoints (Phase 2) ---

export interface ProactiveCase {
  case_id: string;
  post_id: number;
  creator_id: number;
  creator_name: string;
  caption_preview: string | null;
  content_description: string | null;
  thumbnail_url: string | null;
  status:
    'pending' | 'heuristic' | 'standard' | 'deep' | 'escalated' | 'approved' | 'rejected' | 'error';
  combined_risk: number | null;
  rule_matched: string | null;
  opened_at: string;
  completed_at: string | null;
}

/** Stable base key for the proactive moderation queue — used for invalidation. */
export const MODERATION_PROACTIVE_KEY = ['moderation', 'proactive'] as const;

/**
 * tipmi-api proactive/coop cases are 0-based (`offset = page * size`); useServerTable is 1-based.
 * Convert both the request page and the response pageNumber so the first UI page is OFFSET 0.
 */
function toZeroBasedPage(pageParam: number): number {
  return Math.max(0, pageParam - 1);
}

function fromZeroBasedPageResponse<T>(page: PageResponse<T> | null | undefined): PageResponse<T> {
  if (!page) return { rows: [], pageNumber: 1, totalPage: 1, totalElements: 0 };
  return {
    ...page,
    pageNumber: (page.pageNumber ?? 0) + 1,
  };
}

/**
 * List: server-paginated proactive moderation queue (Coop Phase 2). Uses the same useServerTable
 * pattern as the report queue for consistent UX (infinite scroll, search, filters).
 */
/**
 * Primary Coop moderation case queue. The admin API route is only the authenticated proxy boundary.
 */
export function useCoopModerationCases(defaultStatus?: string) {
  return useServerTable<ProactiveCase>({
    queryKey: ['moderation', 'coop', 'cases', defaultStatus ?? 'active'],
    fetchPage: async ({ pageParam, pageSize, filters }) => {
      const status = filters.status?.[0] ?? defaultStatus ?? 'active';
      const params: Record<string, string | number> = {
        page: toZeroBasedPage(pageParam),
        size: pageSize,
      };
      if (status) params.status = status;

      const res = await http.get<SuccessResponseDTO<PageResponse<ProactiveCase>>>(
        '/admin/api/moderation/coop/cases',
        { params },
      );
      return fromZeroBasedPageResponse(selectData<PageResponse<ProactiveCase>>(res.data));
    },
    pageSize: 20,
    mode: 'paged',
    // Queue and History are two grids backed by this one hook, so the URL key is namespaced by
    // status to stop their page numbers overwriting each other.
    urlKey: `coop-${defaultStatus ?? 'queue'}`,
  });
}

/**
 * @deprecated Use `useCoopModerationCases`. This hook reads the legacy route alias.
 */
export function useModerationProactive(defaultStatus?: string) {
  return useServerTable<ProactiveCase>({
    queryKey: [...MODERATION_PROACTIVE_KEY, defaultStatus ?? 'all'],
    fetchPage: async ({ pageParam, pageSize, filters }) => {
      const status = filters.status?.[0] ?? defaultStatus ?? '';
      const params: Record<string, string | number> = {
        page: toZeroBasedPage(pageParam),
        size: pageSize,
      };
      if (status) params.status = status;

      const res = await http.get<SuccessResponseDTO<PageResponse<ProactiveCase>>>(
        '/admin/api/moderation/proactive',
        { params },
      );
      return fromZeroBasedPageResponse(selectData<PageResponse<ProactiveCase>>(res.data));
    },
    pageSize: 20,
  });
}

// --- Moderation Actions Log ---

export interface ModerationActionEntry {
  id: string;
  timestamp: string | null;
  action_type: string; // approved, rejected, takedown, ban, etc.
  actor: string; // pipeline or human
  actor_name: string | null;
  target_type: string; // post, user, comment, message
  target_id: string | null;
  target_preview: string | null;
  target_thumbnail: string | null;
  reason: string | null;
  rule_name: string | null;
  ai_description: string | null;
}

/** Stable key for the actions log. */
export const MODERATION_ACTIONS_KEY = ['moderation', 'actions'] as const;

/**
 * Primary Coop action history. Actions are derived from completed Coop cases by the admin proxy.
 */
export function useCoopModerationActions() {
  return useServerTable<ModerationActionEntry>({
    queryKey: ['moderation', 'coop', 'actions'],
    fetchPage: async ({ pageParam, pageSize, filters }) => {
      // Same 0-based tipmi-api paging as coop/cases (actions are derived from those cases).
      const params: Record<string, string | number> = {
        page: toZeroBasedPage(pageParam),
        size: pageSize,
      };
      const actionType = filters.actionType?.[0];
      if (actionType) params.actionType = actionType;
      const actor = filters.actor?.[0];
      if (actor) params.actor = actor;

      const res = await http.get<SuccessResponseDTO<PageResponse<ModerationActionEntry>>>(
        '/admin/api/moderation/coop/actions',
        { params },
      );
      return fromZeroBasedPageResponse(selectData<PageResponse<ModerationActionEntry>>(res.data));
    },
    pageSize: 20,
    mode: 'paged',
    urlKey: 'coop-actions',
  });
}

/**
 * @deprecated Use `useCoopModerationActions`. This hook reads the legacy route alias.
 */
export function useModerationActionLog() {
  return useServerTable<ModerationActionEntry>({
    queryKey: MODERATION_ACTIONS_KEY,
    fetchPage: async ({ pageParam, pageSize, filters }) => {
      const params: Record<string, string | number> = {
        page: toZeroBasedPage(pageParam),
        size: pageSize,
      };
      const actionType = filters.actionType?.[0];
      if (actionType) params.actionType = actionType;
      const actor = filters.actor?.[0];
      if (actor) params.actor = actor;

      const res = await http.get<SuccessResponseDTO<PageResponse<ModerationActionEntry>>>(
        '/admin/api/moderation/actions',
        { params },
      );
      return fromZeroBasedPageResponse(selectData<PageResponse<ModerationActionEntry>>(res.data));
    },
    pageSize: 20,
  });
}

// --- Moderation Rules ---

export interface ModerationRule {
  rule_id: string;
  rule_name: string;
  description: string;
  condition_summary: string;
  action_type: string; // reject, escalate, approve
  /** Human-readable description of what happens when this rule triggers. */
  enforcement_description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  enabled: boolean;
  /** The threshold value that can be edited (0-1 scale). */
  threshold?: number | null | undefined;
  triggers_today?: number | undefined;
  triggers_total?: number | undefined;
  false_positive_rate?: number | null | undefined;
  last_triggered?: string | null | undefined;
}

/**
 * Primary Coop policy rules.
 */
export function useCoopModerationRules() {
  return useQuery({
    queryKey: ['moderation', 'coop', 'rules'],
    queryFn: async (): Promise<ModerationRule[]> => {
      const res = await http.get<SuccessResponseDTO<ModerationRule[]>>(
        '/admin/api/moderation/coop/rules',
      );
      return selectData<ModerationRule[]>(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

/** @deprecated Use `useCoopModerationRules`; this reads the legacy route alias. */
export function useModerationRules() {
  return useQuery({
    queryKey: ['moderation', 'rules'],
    queryFn: async (): Promise<ModerationRule[]> => {
      const res = await http.get<SuccessResponseDTO<ModerationRule[]>>(
        '/admin/api/moderation/rules',
      );
      return selectData<ModerationRule[]>(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

export interface UpdateRuleInput {
  rule_id: string;
  threshold?: number | undefined;
  enabled?: boolean | undefined;
  action_type?: string | undefined;
}

/** PATCH /admin/api/moderation/coop/rules/{ruleId} — update Coop rule threshold/enabled state. */
export function useUpdateCoopModerationRule() {
  return useMutation({
    mutationFn: async (data: UpdateRuleInput) => {
      const res = await http.patch<SuccessResponseDTO<void>>(
        `/admin/api/moderation/coop/rules/${data.rule_id}`,
        data,
      );
      return selectData<void>(res.data);
    },
  });
}

/** @deprecated Use `useUpdateCoopModerationRule`; this writes through the legacy route alias. */
export function useUpdateModerationRule() {
  return useMutation({
    mutationFn: async (data: UpdateRuleInput) => {
      const res = await http.patch<SuccessResponseDTO<void>>(
        `/admin/api/moderation/rules/${data.rule_id}`,
        data,
      );
      return selectData<void>(res.data);
    },
  });
}

// --- Moderation Dashboard (TM-3450) ---

function dashboardBoundsFromRange(
  range: TimeRangeBounds | number | null | undefined,
): TimeRangeBounds {
  if (range == null || typeof range === 'number') {
    return range == null ? {} : { days: range };
  }
  return range;
}

/** GET /admin/api/moderation/dashboard — summary KPIs + module shells. */
export function useModerationDashboard(
  range: TimeRangeBounds | number | null | undefined,
  geoSelection?: string,
) {
  const bounds = dashboardBoundsFromRange(range);
  const country = apiCountryCode(geoSelection ?? '');
  const region = geoRegionCode(geoSelection ?? '');
  const params: Record<string, string | number> = {};
  if (bounds.days != null) params.days = bounds.days;
  if (bounds.startDate) params.startDate = bounds.startDate;
  if (bounds.endDate) params.endDate = bounds.endDate;
  if (country) params.countryCode = country;
  if (region) params.regionCode = region;

  return useQuery({
    queryKey: ['/admin/api/moderation/dashboard', params],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<ModerationDashboardResponseDTO>>({
        url: '/admin/api/moderation/dashboard',
        method: 'GET',
        params,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
  });
}

/**
 * TM-3756 Phase B2 — admin-configurable Policy confidence thresholds (proxied to media-manager;
 * tipmi-admin-api falls back to hardcoded defaults when media-manager is unreachable).
 */
export interface PolicyConfidenceThreshold {
  policyName: string;
  /** Minimum attribution confidence (0–1) for the Policy to be treated as violated. */
  attribution: number | null;
  routingClearLt: number | null;
  routingFrontierLt: number | null;
  routingHumanLt: number | null;
  /** Hard minimum attribution (CSAM-class); null when none. */
  safetyFloor: number | null;
  /** When true, feedback-driven adjustments are skipped. */
  manualLock: boolean | null;
  /** Pending feedback-loop proposal (shadow mode); shape is owned by media-manager. */
  shadowProposal?: Record<string, unknown> | null;
  lastManualEdit?: Record<string, unknown> | null;
  lastFeedbackAdjustment?: Record<string, unknown> | null;
}

export interface UpdatePolicyConfidenceThresholdInput {
  policyName: string;
  attribution?: number | undefined;
  routingClearLt?: number | undefined;
  routingFrontierLt?: number | undefined;
  routingHumanLt?: number | undefined;
  manualLock?: boolean | undefined;
}

export const POLICY_CONFIDENCE_THRESHOLDS_KEY = ['moderation', 'policy-thresholds'] as const;

/** GET /admin/api/moderation/policy-thresholds — thresholds for the 12 Coop leaf Policies. */
export function usePolicyConfidenceThresholds() {
  return useQuery({
    queryKey: POLICY_CONFIDENCE_THRESHOLDS_KEY,
    queryFn: async (): Promise<PolicyConfidenceThreshold[]> => {
      const res = await http.get<SuccessResponseDTO<PolicyConfidenceThreshold[]>>(
        '/admin/api/moderation/policy-thresholds',
      );
      return selectData<PolicyConfidenceThreshold[]>(res.data) ?? [];
    },
    staleTime: 60_000,
  });
}

/**
 * PATCH /admin/api/moderation/policy-thresholds?policyName=… — partial update of one Policy.
 * The Policy name goes in a query param (not the path) because names contain "/" and encoded
 * slashes in path segments are rejected by the servlet container.
 */
export function useUpdatePolicyConfidenceThreshold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ policyName, ...body }: UpdatePolicyConfidenceThresholdInput) => {
      const res = await http.patch<SuccessResponseDTO<PolicyConfidenceThreshold>>(
        '/admin/api/moderation/policy-thresholds',
        body,
        { params: { policyName } },
      );
      return selectData<PolicyConfidenceThreshold>(res.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POLICY_CONFIDENCE_THRESHOLDS_KEY });
    },
  });
}
